/**
 * CompactCteStrip — the WITH clause of the compact popover, as a slim chip row.
 *
 * WHY a strip rather than the full editor's `CteSection`: the compact popover
 * no longer degrades a `WITH … SELECT` (spec 2026-07-28), so it needs a CTE
 * affordance — but a stack of `CteCard`s would eat the whole popover before the
 * main query's clause cards are reached. One chip per binding keeps the WITH
 * clause to a single line; clicking a chip DRILLS into that CTE body through
 * the LOCAL compact drill (`subqueryCtx.onDrill` — the same wiring the WHERE
 * subquery chips use, never the global vqeStore drill stack), where
 * `CompactNestedSections` renders full clause editing over the body's own edit
 * context. That is why the chip is the only per-binding control here: rename /
 * remove / body preview all live one level in, or in the full editor. Only a
 * SELECT-shaped body is drillable (`CteCard`'s `canDrillBody` parity) — see the
 * `canDrill` comment below for the data-modifying-CTE hazard it closes.
 *
 * `density='compact'` (the ClauseCard single-line primitive) is deliberate: the
 * body is one wrappable chip row, so the strip claims a row — not a card — of
 * the popover's vertical budget. The footer reuses `CteAddPopover` (it handles
 * BOTH append-to-existing-WITH and WITH-from-scratch), so once a WITH exists
 * the strip owns add-CTE and the header kebab stands down.
 */
import { ChevronRight } from 'lucide-react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { VqeEditContext } from '../../edit/editContext';
import { ClauseCard } from '../../render/select/ClauseCard';
import { VqeTooltip } from '../../render/common/VqeTooltip';
import { CteAddPopover } from '../../render/select/CteAddPopover';
import type { CteBinding, CteOutline } from '../../render/select/extractCtes';
import type { SubqueryWiring } from '../../render/select/SelectRenderer';
import { sanitizeIdentifier, truncateIdentifier } from '../../ux/drillLabel';
import { vqeCopy } from '../../ux/vqeCopy';

export interface CompactCteStripProps {
  readonly outline: CteOutline;
  readonly editContext?: VqeEditContext;
  /** Local compact drill wiring; every chip disables without it (defensive). */
  readonly subqueryCtx?: SubqueryWiring;
}

export function CompactCteStrip({ outline, editContext, subqueryCtx }: CompactCteStripProps) {
  const { track } = useAnalytics();
  const onDrill = subqueryCtx?.onDrill;
  // Mirrors `CteSection`'s gate: a bare edit context exists before the async
  // schema refresh lands, and the add-CTE form needs the snapshot.
  const editReady = !!editContext && !!editContext.service.getSchemaSnapshot();

  if (!outline.hasWith) return null;

  // Parity with `CteCard`'s `canDrillBody`: only a SELECT-shaped body opens in
  // the structured editor. `CompactNestedSections` force-classifies a drilled
  // body as a SELECT, so an enabled chip on a data-modifying CTE
  // (`WITH d AS (DELETE … RETURNING …)`) would mount empty clause cards — whose
  // edit affordances commit into that body's byte range — over a DELETE.
  // (`extractCtes` maps a VALUES-only body to 'select' by design, so those stay
  // drillable here exactly as they are in the full editor.)
  const canDrill = (binding: CteBinding): boolean => !!onDrill && binding.bodyKind === 'select';

  const drillInto = (binding: CteBinding): void => {
    if (!subqueryCtx || !onDrill || !canDrill(binding)) return;
    // No CTE name in the props — identifiers are PII-adjacent (analytics rule).
    track('feature:vqe_compact_cte_drill');
    // Same collapse+truncate treatment `buildDrillLabel` applies to every other
    // drill-breadcrumb identifier (FilterPills, CteCard, …) — a CTE name can
    // come from a quoted identifier carrying internal whitespace/newlines, and
    // this label has no length cap of its own since it doesn't go through
    // `buildDrillLabel` (its "<KEYWORD> <ident>" shape doesn't fit the
    // "<name> (CTE)" format this strip wants).
    const label = `${truncateIdentifier(sanitizeIdentifier(binding.name))} (CTE)`;
    onDrill({
      tabId: subqueryCtx.drillTabId,
      parentStatementId: subqueryCtx.statementId,
      bodyStart: binding.bodyStart,
      bodyEnd: binding.bodyEnd,
      label,
    });
  };

  return (
    <ClauseCard
      title={outline.isRecursive ? 'With (recursive)' : 'With'}
      testId="vqe-compact-cte-strip"
      density="compact"
      footer={<CteAddPopover editContext={editReady ? editContext : undefined} />}
    >
      <div className="flex flex-wrap items-center gap-1.5" data-testid="vqe-compact-cte-list">
        {outline.bindings.map((binding, idx) => (
          <VqeTooltip
            key={`${binding.name}-${idx}`}
            content={
              canDrill(binding)
                ? vqeCopy.cte.focusBody
                : binding.bodyKind === 'select'
                  ? vqeCopy.disabled.editingUnavailable
                  : vqeCopy.disabled.cteBodyEditingUnavailable
            }
            shrink
          >
            <button
              type="button"
              disabled={!canDrill(binding)}
              onClick={() => drillInto(binding)}
              className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-0.5 font-mono text-[11px] text-foreground hover:bg-accent disabled:cursor-default disabled:opacity-60 disabled:hover:bg-muted/30"
              data-testid={`vqe-compact-cte-chip-${binding.name}`}
              data-recursive={binding.isRecursive ? 'true' : 'false'}
              data-body-kind={binding.bodyKind}
              aria-label={`Edit CTE ${binding.name}`}
            >
              <span className="truncate">{binding.name}</span>
              {binding.isRecursive ? (
                <span className="shrink-0 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                  recursive
                </span>
              ) : null}
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            </button>
          </VqeTooltip>
        ))}
      </div>
    </ClauseCard>
  );
}
